import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FOUNDERS, TEAM_LEADS, type FounderEntry, type TeamLead } from "@/lib/brand";
import { useLocalized } from "@/hooks/useLocalized";
import { useLanguage } from "@/contexts/LanguageContext";
import { fadeUp, staggerContainer, blurClear } from "@/lib/animations";

const HeroQuote = ({ founder }: { founder: FounderEntry }) => {
    const { t } = useTranslation();
    const { lang } = useLanguage();
    const quote = useLocalized({ ar: founder.quoteAr, en: founder.quoteEn });
    const name = lang === "en" ? founder.nameEn : founder.nameAr;
    const title = lang === "en" ? founder.titleEn : founder.titleAr;
    const background = useLocalized({ ar: founder.backgroundAr, en: founder.backgroundEn });

    return (
        <motion.blockquote
            variants={blurClear}
            className="relative max-w-5xl mx-auto"
        >
            <span
                aria-hidden
                className="absolute -top-6 -start-2 sm:-start-6 font-v2-display text-7xl sm:text-9xl md:text-[10rem] leading-none select-none pointer-events-none"
                style={{
                    backgroundImage: "var(--gradient-accent-v2)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                    opacity: 0.5,
                }}
            >
                {lang === "ar" ? "“" : "“"}
            </span>

            <p className="font-v2-display text-white text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-snug rtl:leading-[1.75] ps-6 sm:ps-12 md:ps-16">
                {quote}
            </p>

            <footer className="mt-8 sm:mt-10 ps-6 sm:ps-12 md:ps-16 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-white/10 pt-5 sm:pt-6">
                <img
                    src={founder.photo}
                    alt={name}
                    loading="lazy"
                    className="w-12 h-12 sm:w-14 sm:h-14 object-cover border border-white/15"
                />
                <div className="min-w-0">
                    <p className="font-v2-display text-base sm:text-lg text-white leading-tight">
                        {name}
                    </p>
                    <p className="font-mono-brutal text-[10px] text-white/55 mt-1">
                        {title} · {background}
                    </p>
                </div>
                <span aria-hidden className="flex-1 h-px bg-white/10 min-w-[40px]" />
                <span className="font-mono-brutal text-[10px] text-white/40">
                    {t("founders_home.role_label")} · 01
                </span>
            </footer>
        </motion.blockquote>
    );
};

const FounderTile = ({ founder, index }: { founder: FounderEntry; index: number }) => {
    const { lang } = useLanguage();
    const background = useLocalized({ ar: founder.backgroundAr, en: founder.backgroundEn });
    const name = lang === "en" ? founder.nameEn : founder.nameAr;
    const title = lang === "en" ? founder.titleEn : founder.titleAr;
    const num = String(index + 1).padStart(2, "0");

    return (
        <motion.article
            variants={fadeUp}
            className="group relative border border-white/10 hover:border-white/25 transition-colors duration-300 p-5 sm:p-6 bg-white/[0.015] flex flex-col gap-4"
        >
            <span
                aria-hidden
                className="absolute top-0 inset-x-0 h-px scale-x-0 group-hover:scale-x-100 origin-start transition-transform duration-500"
                style={{ background: "var(--gradient-accent-v2)" }}
            />

            <div className="flex items-center justify-between">
                <span className="font-mono-brutal text-[10px] text-white/40 tabular-nums">
                    [{num}]
                </span>
                <span className="font-mono-brutal text-[10px] text-white/40 truncate max-w-[60%]">
                    {background}
                </span>
            </div>

            <img
                src={founder.photo}
                alt={name}
                loading="lazy"
                className="w-full aspect-square object-cover border border-white/10 grayscale-[40%] group-hover:grayscale-0 transition-all duration-500"
            />

            <div>
                <h4 className="font-v2-display text-base sm:text-lg text-white leading-tight">
                    {name}
                </h4>
                <p className="font-mono-brutal text-[10px] text-white/50 mt-1.5">
                    {title}
                </p>
            </div>
        </motion.article>
    );
};

const TeamLeadTile = ({ lead, index }: { lead: TeamLead; index: number }) => {
    const { lang } = useLanguage();
    const background = useLocalized({ ar: lead.backgroundAr, en: lead.backgroundEn });
    const name = lang === "en" ? lead.nameEn : lead.nameAr;
    const title = lang === "en" ? lead.titleEn : lead.titleAr;
    const num = String(index + 1).padStart(2, "0");

    return (
        <motion.div
            variants={fadeUp}
            className="group relative border border-white/10 hover:border-white/25 transition-colors duration-300 p-5 sm:p-6 bg-white/[0.015] flex flex-col gap-4"
        >
            <span
                aria-hidden
                className="absolute top-0 inset-x-0 h-px scale-x-0 group-hover:scale-x-100 origin-start transition-transform duration-500"
                style={{ background: "var(--gradient-accent-v2)" }}
            />

            <div className="flex items-center justify-between">
                <span className="font-mono-brutal text-[10px] text-white/40 tabular-nums">
                    [{num}]
                </span>
                <span className="font-mono-brutal text-[10px] text-white/40 truncate max-w-[60%]">
                    {background}
                </span>
            </div>

            <img
                src={lead.photo}
                alt={name}
                loading="lazy"
                className="w-full aspect-square object-cover border border-white/10 grayscale-[40%] group-hover:grayscale-0 transition-all duration-500"
            />

            <div>
                <h4 className="font-v2-display text-base text-white leading-tight">
                    {name}
                </h4>
                <p className="font-mono-brutal text-[10px] text-white/50 mt-1.5">
                    {title}
                </p>
            </div>
        </motion.div>
    );
};

const FounderSection = () => {
    const { t } = useTranslation();
    const lead = FOUNDERS[0];
    const restFounders = FOUNDERS.slice(1);

    return (
        <section
            id="founder"
            className="py-12 sm:py-16 md:py-24 lg:py-28"
            aria-labelledby="founder-heading"
        >
            <div className="container mx-auto px-5 sm:px-6 mb-12 sm:mb-16">
                <div className="flex items-center gap-3 mb-6 sm:mb-8">
                    <span aria-hidden className="brutal-glyph" style={{ height: "0.65em" }} />
                    <span className="font-mono-brutal text-[11px] text-white/60">
                        {t("founders_home.eyebrow")}
                    </span>
                    <span aria-hidden className="flex-1 h-px bg-white/10" />
                </div>

                <h2
                    id="founder-heading"
                    className="font-v2-display text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] max-w-4xl mb-12 sm:mb-16 lg:mb-20"
                >
                    {t("founders_home.title_prefix")}{" "}
                    <span
                        style={{
                            backgroundImage: "var(--gradient-accent-v2)",
                            WebkitBackgroundClip: "text",
                            backgroundClip: "text",
                            color: "transparent",
                        }}
                    >
                        {t("founders_home.title_highlight")}
                    </span>
                </h2>

                <motion.div
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.2 }}
                    variants={staggerContainer(0.15, 0.1)}
                >
                    <HeroQuote founder={lead} />
                </motion.div>
            </div>

            {/* Other founders — brutal grid */}
            {restFounders.length > 0 && (
                <div className="container mx-auto px-5 sm:px-6 mb-12 sm:mb-16">
                    <div className="flex items-center gap-3 mb-6 sm:mb-8">
                        <span className="font-mono-brutal text-[10px] text-white/40">
                            CO-FOUNDERS
                        </span>
                        <span aria-hidden className="flex-1 h-px bg-white/8" />
                    </div>
                    <motion.div
                        variants={staggerContainer(0.08, 0.05)}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, amount: 0.1 }}
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/8"
                    >
                        {restFounders.map((founder, i) => (
                            <FounderTile key={founder.key} founder={founder} index={i + 1} />
                        ))}
                    </motion.div>
                </div>
            )}

            {/* Team leads — brutal grid */}
            {TEAM_LEADS.length > 0 && (
                <div className="container mx-auto px-5 sm:px-6">
                    <div className="flex items-center gap-3 mb-6 sm:mb-8">
                        <span className="font-mono-brutal text-[10px] text-white/40">
                            {t("founders_home.leads_label", { defaultValue: "TEAM LEADS" })}
                        </span>
                        <span aria-hidden className="flex-1 h-px bg-white/8" />
                    </div>
                    <motion.div
                        variants={staggerContainer(0.08, 0.05)}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, amount: 0.1 }}
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/8"
                    >
                        {TEAM_LEADS.map((lead, i) => (
                            <TeamLeadTile key={lead.key} lead={lead} index={i} />
                        ))}
                    </motion.div>
                </div>
            )}
        </section>
    );
};

export default FounderSection;
